import { Alert, Button } from '@mantine/core';
import { IconFlame } from '@tabler/icons-react';
import { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { followLink, useShell } from '../../shell-context.js';
import type { VoyageSource } from './source.js';

/**
 * Sefer raporu formunun yakıt defteri durumu (ADR 0117 §2.4, Ek Y3): defteri açık gemide ve
 * açılıştan sonraki raporda ROB/yakıt sütunları defterden türetilir ve rapor gönderilince yazılır;
 * formda salt okunurdur. Tüketim satırları Yakıt Yönetimi ▸ Tüketim'de (aynı raporun bölümü)
 * girilir. Defter açık değilse form bugünkü gibidir (§13).
 */
export interface FuelLedgerState {
  /** Bu raporun yakıt sütunları defterden mi. */
  active: boolean;
  /** Tüketim ekranının bu rapora bağlantısı (rapor kaydedilmişse). */
  consumptionHref: string | null;
}

const NONE: FuelLedgerState = { active: false, consumptionHref: null };

export const FuelLedgerContext = createContext<FuelLedgerState>(NONE);

export function useFuelLedger(): FuelLedgerState {
  return useContext(FuelLedgerContext);
}

/** Geminin defter başlangıcı (yoksa `null`); kaynak vermiyorsa defter yok sayılır. */
export function useFuelLedgerStart(source: VoyageSource, vesselId: string | null): string | null {
  const [start, setStart] = useState<string | null>(null);
  useEffect(() => {
    setStart(null);
    if (!vesselId || !source.fuelLedgerStart) return;
    let live = true;
    source.fuelLedgerStart(vesselId).then(
      (value) => {
        if (live) setStart(value);
      },
      () => {
        // Yetki ya da eski sunucu: form bugünkü gibi kalır
      },
    );
    return () => {
      live = false;
    };
  }, [source, vesselId]);
  return start;
}

/** Raporun defter durumu: rapor açılıştan sonraysa etkin (§13). */
export function fuelLedgerState(
  start: string | null,
  reportedAt: string | null | undefined,
  reportId: string | null,
  vesselId: string | null,
  scope: 'ship' | 'web',
): FuelLedgerState {
  if (!start || !reportedAt || Number.isNaN(Date.parse(reportedAt))) return NONE;
  if (Date.parse(reportedAt) <= Date.parse(start)) return NONE;
  const query = new URLSearchParams();
  if (scope === 'web' && vesselId) query.set('vesselId', vesselId);
  if (reportId) query.set('report', reportId);
  return {
    active: true,
    consumptionHref: reportId ? `/fuel/consumption?${query.toString()}` : null,
  };
}

/** Formdaki not: ROB ve tüketim defterden; tüketim satırları Tüketim ekranında. */
export function FuelLedgerNote() {
  const { t } = useTranslation();
  const shell = useShell();
  const ledger = useFuelLedger();
  if (!ledger.active) return null;
  const href = ledger.consumptionHref;
  return (
    <Alert color="blue" role="note" icon={<IconFlame size={18} />}>
      {t('fuel.consumption.formNote')}
      {href ? (
        <>
          {' '}
          <Button
            component="a"
            href={href}
            variant="subtle"
            size="compact-sm"
            onClick={(event: React.MouseEvent<HTMLAnchorElement>) => {
              if (shell) followLink(event, href, shell.navigate);
            }}
          >
            {t('fuel.consumption.openConsumption')}
          </Button>
        </>
      ) : null}
    </Alert>
  );
}
